/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import { mcpServersAddToolMetadataBatch } from "../funcs/mcpServersAddToolMetadataBatch.js";
import { mcpServersCreate } from "../funcs/mcpServersCreate.js";
import { mcpServersDelete } from "../funcs/mcpServersDelete.js";
import { mcpServersDeleteToolMetadata } from "../funcs/mcpServersDeleteToolMetadata.js";
import { mcpServersGet } from "../funcs/mcpServersGet.js";
import { mcpServersGetUsageImpact } from "../funcs/mcpServersGetUsageImpact.js";
import { mcpServersList } from "../funcs/mcpServersList.js";
import { mcpServersListForOrg } from "../funcs/mcpServersListForOrg.js";
import { mcpServersListToolFilters } from "../funcs/mcpServersListToolFilters.js";
import { mcpServersListToolMetadata } from "../funcs/mcpServersListToolMetadata.js";
import { mcpServersSetToolMetadata } from "../funcs/mcpServersSetToolMetadata.js";
import { mcpServersSetToolMetadataBatch } from "../funcs/mcpServersSetToolMetadataBatch.js";
import { mcpServersUpdate } from "../funcs/mcpServersUpdate.js";
import { ClientSDK, RequestOptions } from "../lib/sdks.js";
import { AddToolMetadataBatchResult } from "../models/components/addtoolmetadatabatchresult.js";
import { ListMcpServersResult } from "../models/components/listmcpserversresult.js";
import { ListToolFiltersResult } from "../models/components/listtoolfiltersresult.js";
import { ListToolMetadataResult } from "../models/components/listtoolmetadataresult.js";
import { McpServer } from "../models/components/mcpserver.js";
import { McpServerUsageImpact } from "../models/components/mcpserverusageimpact.js";
import { SetToolMetadataBatchResult } from "../models/components/settoolmetadatabatchresult.js";
import { ToolMetadata } from "../models/components/toolmetadata.js";
import {
  AddMcpServerToolMetadataBatchRequest,
  AddMcpServerToolMetadataBatchSecurity,
} from "../models/operations/addmcpservertoolmetadatabatch.js";
import {
  CreateMcpServerRequest,
  CreateMcpServerSecurity,
} from "../models/operations/createmcpserver.js";
import {
  DeleteMcpServerRequest,
  DeleteMcpServerSecurity,
} from "../models/operations/deletemcpserver.js";
import {
  DeleteMcpServerToolMetadataRequest,
  DeleteMcpServerToolMetadataSecurity,
} from "../models/operations/deletemcpservertoolmetadata.js";
import {
  GetMcpServerRequest,
  GetMcpServerSecurity,
} from "../models/operations/getmcpserver.js";
import {
  GetMcpServerUsageImpactRequest,
  GetMcpServerUsageImpactSecurity,
} from "../models/operations/getmcpserverusageimpact.js";
import {
  ListMcpServersRequest,
  ListMcpServersSecurity,
} from "../models/operations/listmcpservers.js";
import {
  ListMcpServersForOrgRequest,
  ListMcpServersForOrgSecurity,
} from "../models/operations/listmcpserversfororg.js";
import {
  ListMcpServerToolFiltersRequest,
  ListMcpServerToolFiltersSecurity,
} from "../models/operations/listmcpservertoolfilters.js";
import {
  ListMcpServerToolMetadataRequest,
  ListMcpServerToolMetadataSecurity,
} from "../models/operations/listmcpservertoolmetadata.js";
import {
  SetMcpServerToolMetadataRequest,
  SetMcpServerToolMetadataSecurity,
} from "../models/operations/setmcpservertoolmetadata.js";
import {
  SetMcpServerToolMetadataBatchRequest,
  SetMcpServerToolMetadataBatchSecurity,
} from "../models/operations/setmcpservertoolmetadatabatch.js";
import {
  UpdateMcpServerRequest,
  UpdateMcpServerSecurity,
} from "../models/operations/updatemcpserver.js";
import { unwrapAsync } from "../types/fp.js";

export class McpServers extends ClientSDK {
  /**
   * addToolMetadataBatch mcpServers
   *
   * @remarks
   * Strictly additive batch insert of tool metadata for an MCP server. Every tool in the payload is inserted; if any of them already has a live stored entry the whole batch fails with a conflict and nothing is inserted. Stored tools absent from the payload are left untouched and nothing is deleted. Callers are expected to send only tools they know are new.
   */
  async addToolMetadataBatch(
    request: AddMcpServerToolMetadataBatchRequest,
    security?: AddMcpServerToolMetadataBatchSecurity | undefined,
    options?: RequestOptions,
  ): Promise<AddToolMetadataBatchResult> {
    return unwrapAsync(mcpServersAddToolMetadataBatch(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * createMcpServer mcpServers
   *
   * @remarks
   * Create a new MCP server
   */
  async create(
    request: CreateMcpServerRequest,
    security?: CreateMcpServerSecurity | undefined,
    options?: RequestOptions,
  ): Promise<McpServer> {
    return unwrapAsync(mcpServersCreate(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * deleteMcpServer mcpServers
   *
   * @remarks
   * Delete an MCP server. A toolset's own hosted server (its id equals the toolset id) is disabled instead, as turning MCP off on the toolset does, and stays listed with visibility disabled. Other servers, including other toolset-backed ones, are deleted.
   */
  async delete(
    request: DeleteMcpServerRequest,
    security?: DeleteMcpServerSecurity | undefined,
    options?: RequestOptions,
  ): Promise<void> {
    return unwrapAsync(mcpServersDelete(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * deleteToolMetadata mcpServers
   *
   * @remarks
   * Soft-delete a single tool metadata entry.
   */
  async deleteToolMetadata(
    request: DeleteMcpServerToolMetadataRequest,
    security?: DeleteMcpServerToolMetadataSecurity | undefined,
    options?: RequestOptions,
  ): Promise<void> {
    return unwrapAsync(mcpServersDeleteToolMetadata(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * getMcpServer mcpServers
   *
   * @remarks
   * Get an MCP server by ID, slug, or backing toolset ID. Exactly one selector must be provided. Toolset lookup prefers enabled servers, then the canonical wrapper, then the oldest server.
   */
  async get(
    request?: GetMcpServerRequest | undefined,
    security?: GetMcpServerSecurity | undefined,
    options?: RequestOptions,
  ): Promise<McpServer> {
    return unwrapAsync(mcpServersGet(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * getMcpServerUsageImpact mcpServers
   *
   * @remarks
   * Preview who a breaking change to an MCP server affects: the server's active signed-in connections and when each subject last used them. Authorizes the caller as updateMcpServer does. Only sessions issued by the server's user session issuer are counted, so servers without sign-in report sign_in_required=false and no subjects.
   */
  async getUsageImpact(
    request: GetMcpServerUsageImpactRequest,
    security?: GetMcpServerUsageImpactSecurity | undefined,
    options?: RequestOptions,
  ): Promise<McpServerUsageImpact> {
    return unwrapAsync(mcpServersGetUsageImpact(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * listMcpServers mcpServers
   *
   * @remarks
   * List MCP servers for a project. Accepts optional remote_mcp_server_id, tunneled_mcp_server_id, toolset_id, or unproxied_mcp_server_id filters to scope the result to a single backend; at most one filter may be supplied since the backends are mutually exclusive.
   */
  async list(
    request?: ListMcpServersRequest | undefined,
    security?: ListMcpServersSecurity | undefined,
    options?: RequestOptions,
  ): Promise<ListMcpServersResult> {
    return unwrapAsync(mcpServersList(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * listMcpServersForOrg mcpServers
   *
   * @remarks
   * List all MCP servers across the organization
   */
  async listForOrg(
    request?: ListMcpServersForOrgRequest | undefined,
    security?: ListMcpServersForOrgSecurity | undefined,
    options?: RequestOptions,
  ): Promise<ListMcpServersResult> {
    return unwrapAsync(mcpServersListForOrg(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * listToolFilters mcpServers
   *
   * @remarks
   * List the tool filter scopes (tags) available on an MCP server and the tools under each, including tools excluded from all filters. Exactly one of id or slug must be provided. Read-only; reflects the explicit tool variations group resolved from the chain (mcp_servers then toolsets), deriving effective tags with the same logic as the runtime ?tags= filter. Returns filtering disabled when no explicit group is set.
   */
  async listToolFilters(
    request?: ListMcpServerToolFiltersRequest | undefined,
    security?: ListMcpServerToolFiltersSecurity | undefined,
    options?: RequestOptions,
  ): Promise<ListToolFiltersResult> {
    return unwrapAsync(mcpServersListToolFilters(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * listToolMetadata mcpServers
   *
   * @remarks
   * List stored tool metadata for an MCP server.
   */
  async listToolMetadata(
    request: ListMcpServerToolMetadataRequest,
    security?: ListMcpServerToolMetadataSecurity | undefined,
    options?: RequestOptions,
  ): Promise<ListToolMetadataResult> {
    return unwrapAsync(mcpServersListToolMetadata(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * setToolMetadata mcpServers
   *
   * @remarks
   * Set the annotation hints of a single tool metadata entry (manual override). This is a full-record replace: omitted hints become unset on the stored record.
   */
  async setToolMetadata(
    request: SetMcpServerToolMetadataRequest,
    security?: SetMcpServerToolMetadataSecurity | undefined,
    options?: RequestOptions,
  ): Promise<ToolMetadata> {
    return unwrapAsync(mcpServersSetToolMetadata(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * setToolMetadataBatch mcpServers
   *
   * @remarks
   * Authoritative batch upsert of tool metadata for an MCP server. Every tool in the payload is upserted and any stored tool absent from the payload is soft-deleted, all in one transaction.
   */
  async setToolMetadataBatch(
    request: SetMcpServerToolMetadataBatchRequest,
    security?: SetMcpServerToolMetadataBatchSecurity | undefined,
    options?: RequestOptions,
  ): Promise<SetToolMetadataBatchResult> {
    return unwrapAsync(mcpServersSetToolMetadataBatch(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * updateMcpServer mcpServers
   *
   * @remarks
   * Update an MCP server. This is a full-record replace for the optional UUID references: fields omitted from the request become null on the stored record. name is an exception — omitting it leaves the existing display name unchanged, while providing it requires a non-empty value and recomputes the server-side slug. The id and visibility fields are required; exactly one of remote_mcp_server_id, tunneled_mcp_server_id, or toolset_id must be provided.
   */
  async update(
    request: UpdateMcpServerRequest,
    security?: UpdateMcpServerSecurity | undefined,
    options?: RequestOptions,
  ): Promise<McpServer> {
    return unwrapAsync(mcpServersUpdate(
      this,
      request,
      security,
      options,
    ));
  }
}
